import { EXPENSE_PERIODS } from '@clockwise/shared';
import { z } from 'zod';

/**
 * Single source of truth for expense-catalog form validation, shared by the
 * create sheet and the dedicated edit route so their rules can't drift.
 * Mirrors the server contract in `packages/clients/src/expense-catalogs/types.ts`
 * and the backend `costAmount` validator, so the form rejects exactly what the
 * API would.
 */

/** Plain non-negative decimal, at most two fractional digits. */
const COST_REGEX = /^\d+(\.\d{1,2})?$/;

/** Digits available left of the decimal point in `numeric(12, 2)`. */
const COST_MAX_WHOLE_DIGITS = 10;

export const ExpenseCatalogFormSchema = z
  .object({
    // `.trim()` before `.min(1)` on both text fields so a whitespace-only entry
    // is caught HERE, inline under the field, instead of passing the client
    // check and coming back as a server 400 the user has to decode.
    name: z.string().trim().min(1, 'Required').max(120, 'Up to 120 characters'),
    description: z.string().trim().min(1, 'Required').max(500, 'Up to 500 characters'),
    /**
     * Held as a string, never a number.
     *
     * `<input type="number">` hands back a string anyway, and keeping it that
     * way all the way to the API means the amount never round-trips through a
     * binary float. It also lets us reject a third decimal place rather than
     * let Postgres silently round it — matching the server, which does the same.
     *
     * Blank is legal at THIS level and rejected by the refinement below unless
     * the item is variable. Keeping the field always-present (rather than
     * making it optional) means the input stays a controlled component when
     * the switch is flipped back and forth, and whatever the user typed before
     * flipping is still there when they flip back.
     */
    cost: z
      .string()
      .regex(COST_REGEX, 'Enter a non-negative amount with at most 2 decimal places')
      .refine(
        (v) => (v.split('.')[0] ?? '').replace(/^0+(?=\d)/, '').length <= COST_MAX_WHOLE_DIGITS,
        `Must be less than ${'9'.repeat(COST_MAX_WHOLE_DIGITS)}`,
      )
      .or(z.literal('')),
    /** A variable item's amount is only known per occurrence, so it carries no
     *  price — see `docs/expense-catalogs.md`. */
    variable: z.boolean(),
    period: z.enum(EXPENSE_PERIODS),
  })
  // Mirrors the server's `checkVariableCost`. Anchored on `cost` so the message
  // lands under the input the user has to change, not at the top of the form.
  .superRefine((v, ctx) => {
    if (!v.variable && v.cost === '') {
      ctx.addIssue({ code: 'custom', path: ['cost'], message: 'Required' });
    }
  });
export type ExpenseCatalogFormValues = z.infer<typeof ExpenseCatalogFormSchema>;

/**
 * What a submitted form sends for `cost`.
 *
 * A variable item posts `null`, not `''` — the API reads null as "no price"
 * and would reject the empty string as a malformed amount. Spelled once here
 * so the create sheet and the edit route cannot disagree.
 */
export function costForSubmit(values: ExpenseCatalogFormValues): string | null {
  return values.variable ? null : values.cost;
}

const FIELDS = [
  'name',
  'description',
  'cost',
  'variable',
  'period',
] as const satisfies readonly (keyof ExpenseCatalogFormValues)[];

/**
 * Narrow a server-side field-error path to a known form field before routing
 * it to `form.setError`.
 *
 * Listed explicitly rather than read off `ExpenseCatalogFormSchema.shape`: the
 * schema is now a `ZodEffects` (it carries a `superRefine`) and has no `.shape`
 * to derive from. `satisfies` above keeps the list honest — a field renamed in
 * the schema and not here fails typecheck.
 */
export function isExpenseCatalogFormField(path: string): path is keyof ExpenseCatalogFormValues {
  return (FIELDS as readonly string[]).includes(path);
}

/**
 * Format a `numeric(12,2)` string from the API for display.
 *
 * Takes the raw string and never `Number(...)`s it for storage — but display
 * needs grouping separators, so it parses defensively here and returns the
 * raw text unchanged when the value isn't a finite number. Rendering a bare
 * `$0.00` for a value we failed to parse would invent a real-looking charge
 * out of a data problem (G15); `Number('')` is exactly that trap.
 */
export function formatCost(cost: string): string {
  const n = Number(cost);
  if (cost.trim() === '' || !Number.isFinite(n)) return cost;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

/** What a table cell or detail row shows in place of an amount for an item
 *  whose cost varies. A word, not an em-dash: "—" reads as missing data, and
 *  this is a deliberate, meaningful state. */
export const VARIABLE_COST_LABEL = 'Variable';

/**
 * Display an amount that may legitimately be absent.
 *
 * `cost` is `null` exactly when the item is variable, so a null is never a
 * data problem to paper over — and never a zero. `formatCost(String(null))`
 * would render "null" and `Number(null)` would render `$0.00`, inventing a
 * free charge out of a real one whose amount simply is not known yet (G15).
 */
export function formatCostOrVariable(cost: string | null): string {
  return cost === null ? VARIABLE_COST_LABEL : formatCost(cost);
}
